/**
 * ClimateChart
 * ------------
 * Renders an interactive multi-panel SVG climate dashboard showing all AgERA5
 * variables across the study area's three landmark reference points.
 *
 * Each panel shows three climatological mean curves (one per landmark site),
 * coloured by habitat: Escarpment (sand), River (blue), Temp Veg (green).
 * Hovering shows a crosshair + tooltip with values for all three locations.
 * A description badge on each card shows what the variable means ecologically.
 */

export interface VarMeta {
  name: string;
  unit: string;
  conv: (v: number) => number;
  description: string;
}

// Three landmark locations — prefix matches column names in the CSV
const LOCATIONS = [
  { prefix: 'escarpment', label: 'Escarpment',    color: '#D0B39C' },
  { prefix: 'river',      label: 'Zambezi River',  color: '#97B8CB' },
  { prefix: 'tempveg',    label: 'Middle River',   color: '#3F8533' },
] as const;

// AgERA5 (projects/climate-engine-pro/assets/ce-ag-era5-v2/daily) band names → display metadata
export const VAR_META: Record<string, VarMeta> = {
  Temperature_Air_2m_Mean_24h: {
    name: 'Mean Temp', unit: '°C', conv: v => v - 273.15,
    description: 'Daily mean air temperature — controls photosynthesis rates and phenology timing across the landscape.',
  },
  Temperature_Air_2m_Min_24h: {
    name: 'Min Temp', unit: '°C', conv: v => v - 273.15,
    description: 'Overnight minimum temperature — determines frost risk and C3/C4 plant dominance in the escarpment zone.',
  },
  Temperature_Air_2m_Max_24h: {
    name: 'Max Temp', unit: '°C', conv: v => v - 273.15,
    description: 'Peak daytime temperature — drives transpiration stress and fire weather risk in the dry season.',
  },
  Dew_Point_Temperature_2m_Mean_24h: {
    name: 'Dew Point', unit: '°C', conv: v => v - 273.15,
    description: 'Dew point temperature — a proxy for atmospheric humidity; affects morning condensation on vegetation and fire spread potential.',
  },
  Precipitation_Flux: {
    name: 'Precipitation', unit: 'mm', conv: v => v,
    description: 'Daily rainfall — the primary driver of seasonal vegetation green-up, soil recharge, and surface water availability.',
  },
  ReferenceET_PenmanMonteith_FAO56: {
    name: 'Evapotranspiration', unit: 'mm', conv: v => v,
    description: 'FAO-56 Penman-Monteith reference evapotranspiration — energy available to move water from soil to atmosphere. When ET exceeds rainfall, vegetation experiences water stress.',
  },
  Solar_Radiation_Flux: {
    name: 'Solar Radiation', unit: 'MJ/m²', conv: v => v / 1_000_000,
    description: 'Incoming solar energy — drives evaporation and photosynthesis. Higher in the dry season when skies are consistently clear.',
  },
  Wind_Speed_10m_Mean_24h: {
    name: 'Wind Speed', unit: 'm/s', conv: v => v,
    description: 'Daily mean wind speed at 10 m — dry-season winds from the Kalahari High accelerate moisture loss and increase fire spread risk.',
  },
  Vapour_Pressure_Mean_24h: {
    name: 'Vapour Pressure', unit: 'hPa', conv: v => v,
    description: 'Atmospheric water vapour pressure — indicates moisture availability. Low vapour pressure in the dry season increases plant water demand.',
  },
  Vapour_Pressure_Deficit_at_Maximum_Temperature: {
    name: 'VPD (max temp)', unit: 'hPa', conv: v => v,
    description: 'Vapour pressure deficit at peak daytime temperature — the "drying power" of the air at its hottest. High VPD forces stomatal closure, reducing photosynthesis.',
  },
  Cloud_Cover_Mean_24h: {
    name: 'Cloud Cover', unit: 'fraction', conv: v => v,
    description: 'Daily mean cloud cover fraction — regulates surface temperature and rainfall probability. High cloud cover in the wet season suppresses diurnal temperature swings.',
  },
  Derived_Relative_Humidity_2m_Max_24h: {
    name: 'RH Max (daily)', unit: '%', conv: v => v,
    description: 'Daily maximum relative humidity — typically occurs around dawn. Indicates overnight dew formation and plant water status at the start of the day.',
  },
  Derived_Relative_Humidity_2m_Min_24h: {
    name: 'RH Min (daily)', unit: '%', conv: v => v,
    description: 'Daily minimum relative humidity — typically occurs in the early afternoon. Drives peak transpiration stress and fire behaviour in the dry season.',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** 14-day centred rolling mean */
function smooth(vals: number[], w = 14): number[] {
  const half = Math.floor(w / 2);
  return vals.map((_, i) => {
    let sum = 0, cnt = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(vals.length - 1, i + half); j++) {
      if (isFinite(vals[j])) { sum += vals[j]; cnt++; }
    }
    return cnt > 0 ? sum / cnt : NaN;
  });
}

function polyPoints(vals: number[], yMin: number, yMax: number,
                    W: number, H: number, pad: number): string {
  const range = yMax - yMin || 1;
  const pts: string[] = [];
  for (let doy = 1; doy <= 365; doy++) {
    const v = vals[doy - 1];
    if (!isFinite(v)) continue;
    const x = pad + ((doy - 1) / 364) * (W - pad * 2);
    const y = H - pad - ((v - yMin) / range) * (H - pad * 2);
    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(' ');
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

// ── Parsed data ───────────────────────────────────────────────────────────────

interface LocMeans {
  prefix: string;
  label:  string;
  color:  string;
  means: Map<string, number[]>;
}

// ── Main class ────────────────────────────────────────────────────────────────

export class ClimateChart {
  private locMeans: LocMeans[] = [];
  private readonly tooltip: HTMLElement;
  private readonly descTooltip: HTMLElement;

  constructor(
    private readonly container: HTMLElement,
    csvText: string,
  ) {
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'sm-cc-tooltip';
    document.body.appendChild(this.tooltip);

    this.descTooltip = document.createElement('div');
    this.descTooltip.className = 'sm-cc-desc-tooltip';
    document.body.appendChild(this.descTooltip);

    this.parse(csvText);
    this.render();
  }

  destroy(): void {
    this.tooltip.remove();
    this.descTooltip.remove();
  }

  // ── CSV parsing ─────────────────────────────────────────────────────────────
  // Expects DOY-mean format: columns doy (1–365) + {prefix}_{band}
  // Produced by export_climate_csv.py — one row per day-of-year, values are
  // the 5-year climatological mean for that DOY.

  private parse(csvText: string): void {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return;
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const doyIdx = headers.indexOf('doy');
    if (doyIdx < 0) return;

    // Initialize raw arrays: prefix → band → float[365]
    const raw = new Map<string, Map<string, number[]>>();
    LOCATIONS.forEach(loc => {
      raw.set(loc.prefix, new Map());
      for (const band of Object.keys(VAR_META)) {
        raw.get(loc.prefix)!.set(band, new Array(365).fill(NaN));
      }
    });

    for (let li = 1; li < lines.length; li++) {
      const cols = lines[li].split(',');
      const doy = parseInt(cols[doyIdx]?.trim().replace(/^"|"$/g, ''), 10);
      if (isNaN(doy) || doy < 1 || doy > 365) continue;

      for (const loc of LOCATIONS) {
        const locMap = raw.get(loc.prefix)!;
        for (const band of Object.keys(VAR_META)) {
          const col = `${loc.prefix}_${band}`;
          const ci  = headers.indexOf(col);
          if (ci < 0) continue;
          const raw_v = parseFloat(cols[ci]);
          if (isNaN(raw_v)) continue;
          locMap.get(band)![doy - 1] = VAR_META[band].conv(raw_v);
        }
      }
    }

    this.locMeans = LOCATIONS.map(loc => {
      const locMap = raw.get(loc.prefix)!;
      const means  = new Map<string, number[]>();
      for (const [band, vals] of locMap) {
        means.set(band, smooth(vals));
      }
      return { prefix: loc.prefix, label: loc.label, color: loc.color, means };
    });
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  private render(): void {
    this.container.innerHTML = '';

    const hasData = this.locMeans.some(lm => lm.means.size > 0);
    if (!hasData) {
      this.container.innerHTML =
        '<div style="color:#6b8091;font-size:11px;padding:16px 0;text-align:center;">' +
        'Climate data loading… run export_climate_csv.py to generate public/data/climate.csv</div>';
      return;
    }

    const vars = Object.keys(VAR_META).filter(band =>
      this.locMeans.some(lm => lm.means.has(band))
    );

    const W = 260, H = 120, PAD = 20;

    vars.forEach(band => {
      const meta = VAR_META[band];

      const allVals = this.locMeans.flatMap(lm => (lm.means.get(band) ?? []).filter(isFinite));
      if (allVals.length === 0) return;
      let yMin = Math.min(...allVals);
      let yMax = Math.max(...allVals);
      if (yMin === yMax) { yMin -= 1; yMax += 1; }
      const yMid = (yMin + yMax) / 2;

      const svg = svgEl('svg', {
        viewBox: `0 0 ${W} ${H}`,
        width: String(W), height: String(H),
      });
      svg.style.overflow = 'visible';

      // Grid lines
      [0.25, 0.5, 0.75].forEach(t => {
        const y = PAD + t * (H - PAD * 2);
        svg.appendChild(svgEl('line', {
          x1: String(PAD), x2: String(W - PAD), y1: String(y), y2: String(y),
          stroke: 'rgba(255,255,255,0.05)', 'stroke-width': '0.5',
        }));
      });

      // Three location lines (climatological mean)
      const lineEls: SVGPolylineElement[] = [];
      for (const lm of this.locMeans) {
        const meanArr = lm.means.get(band);
        if (!meanArr) continue;
        const pts = polyPoints(meanArr, yMin, yMax, W, H, PAD);
        if (!pts) continue;
        const pl = svgEl('polyline', {
          points: pts, fill: 'none',
          stroke: lm.color, 'stroke-width': '2', opacity: '0.9',
        });
        svg.appendChild(pl);
        lineEls.push(pl);
      }

      // Crosshair
      const xhair = svgEl('line', {
        y1: String(PAD), y2: String(H - PAD),
        stroke: 'rgba(212,179,70,0.7)', 'stroke-width': '1',
      });
      xhair.style.display = 'none';
      svg.appendChild(xhair);

      // Y-axis labels
      ([[yMin, H - PAD], [yMid, H / 2], [yMax, PAD]] as [number, number][]).forEach(([val, y]) => {
        const t = svgEl('text', {
          x: String(PAD - 3), y: String(y),
          'text-anchor': 'end', 'dominant-baseline': 'middle',
          'font-size': '7', fill: '#6b8091',
        });
        t.textContent = val.toFixed(1);
        svg.appendChild(t);
      });

      // X-axis ticks
      ([['Jan', 1], ['Apr', 91], ['Jul', 183], ['Oct', 274], ['Dec', 335]] as [string, number][]).forEach(([lbl, doy]) => {
        const x = PAD + ((doy - 1) / 364) * (W - PAD * 2);
        const t = svgEl('text', {
          x: String(x), y: String(H - PAD + 9),
          'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091',
        });
        t.textContent = lbl;
        svg.appendChild(t);
      });

      // Hover zone
      const zone = svgEl('rect', {
        x: String(PAD), y: String(PAD),
        width: String(W - PAD * 2), height: String(H - PAD * 2),
        fill: 'transparent',
      });
      zone.style.cursor = 'crosshair';
      svg.appendChild(zone);

      zone.addEventListener('mousemove', (e) => {
        const rect  = svg.getBoundingClientRect();
        const xFrac = (e.clientX - rect.left - PAD) / (W - PAD * 2);
        const doy   = Math.round(1 + xFrac * 364);
        if (doy < 1 || doy > 365) return;
        const xPx = PAD + xFrac * (W - PAD * 2);
        xhair.setAttribute('x1', String(xPx)); xhair.setAttribute('x2', String(xPx));
        xhair.style.display = '';

        const dateDisp = new Date(2024, 0, doy).toLocaleDateString('en', { month: 'short', day: 'numeric' });
        const rows = this.locMeans.map(lm => {
          const v = lm.means.get(band)?.[doy - 1] ?? NaN;
          return `<div class="sm-cc-tt-row">
            <span class="sm-cc-tt-key" style="color:${lm.color}">${lm.label}</span>
            ${isFinite(v) ? v.toFixed(2) : '—'} ${meta.unit}
          </div>`;
        }).join('');
        this.tooltip.innerHTML =
          `<div class="sm-cc-tt-title">${meta.name} · ${dateDisp}</div>${rows}`;
        this.tooltip.style.display = 'block';
        this.tooltip.style.left = `${e.pageX + 14}px`;
        this.tooltip.style.top  = `${e.pageY - 10}px`;
      });

      zone.addEventListener('mouseleave', () => {
        xhair.style.display = 'none';
        this.tooltip.style.display = 'none';
      });

      // Card
      const card = document.createElement('div');
      card.className = 'sm-cc-card';

      // Title row with description icon
      const titleRow = document.createElement('div');
      titleRow.className = 'sm-cc-card-title-row';

      const title = document.createElement('div');
      title.className = 'sm-cc-card-title';
      title.textContent = `${meta.name} (${meta.unit})`;

      const infoIcon = document.createElement('span');
      infoIcon.className = 'sm-cc-info-icon';
      infoIcon.textContent = '?';
      infoIcon.title = meta.description;

      const positionDescTooltip = (e: MouseEvent) => {
        const x = e.pageX;
        const y = e.pageY;
        this.descTooltip.style.left = `${x + 12}px`;
        this.descTooltip.style.top  = `${y - 10}px`;
        // Flip to left-side if tooltip overflows right edge of viewport
        const rect = this.descTooltip.getBoundingClientRect();
        if (rect.right > window.innerWidth - 16) {
          this.descTooltip.style.left = `${x - rect.width - 12}px`;
        }
      };

      infoIcon.addEventListener('mouseenter', (e) => {
        this.descTooltip.textContent = meta.description;
        this.descTooltip.style.display = 'block';
        positionDescTooltip(e as MouseEvent);
      });
      infoIcon.addEventListener('mousemove', (e) => {
        positionDescTooltip(e as MouseEvent);
      });
      infoIcon.addEventListener('mouseleave', () => {
        this.descTooltip.style.display = 'none';
      });

      titleRow.appendChild(title);
      titleRow.appendChild(infoIcon);
      card.appendChild(titleRow);
      card.appendChild(svg);
      this.container.appendChild(card);
    });
  }
}
